import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SALON_INFO } from "../data/assets";

const NAV = [
  { id: "home", key: "home" },
  { id: "about", key: "about" },
  { id: "services", key: "services" },
  { id: "reviews", key: "reviews" },
  { id: "gallery", key: "gallery" },
  { id: "hours", key: "hours" },
  { id: "contact", key: "contact" },
];

export const Navbar = () => {
  const { lang, toggleLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/5 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => go("home")}
          className="flex items-center gap-2 group"
          data-testid="logo-button"
        >
          <span className="font-display text-2xl md:text-3xl text-white tracking-tight">
            <span className="text-[#C5A059]">L</span>uxury
          </span>
          <span className="hidden sm:inline font-display text-xl md:text-2xl text-white/60 italic">
            Salón
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[#C5A059] transition-colors duration-300"
              data-testid={`nav-${item.id}`}
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3 md:gap-5">
          {/* Lang switch */}
          <button
            onClick={toggleLang}
            className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#C5A059] transition-colors"
            data-testid="lang-toggle"
            aria-label="Change language"
          >
            <span className={lang === "sk" ? "text-[#C5A059]" : ""}>SK</span>
            <span className="text-white/30">/</span>
            <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
          </button>

          {/* Reserve / call */}
          <a
            href={SALON_INFO.notino}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 bg-[#C5A059] text-black text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#E8D3A6] transition-colors duration-300"
            data-testid="navbar-reserve-btn"
          >
            {t.nav.reserve}
          </a>

          <a
            href={`tel:${SALON_INFO.phoneIntl}`}
            className="md:hidden p-2 text-[#C5A059]"
            data-testid="navbar-call-btn-mobile"
            aria-label="Call"
          >
            <Phone size={20} strokeWidth={1.5} />
          </a>

          {/* Mobile menu */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-1"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  onClick={() => go(item.id)}
                  className="text-left font-display text-2xl text-white hover:text-[#C5A059] transition-colors"
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {t.nav[item.key]}
                </button>
              ))}
              <a
                href={SALON_INFO.notino}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center bg-[#C5A059] text-black text-xs uppercase tracking-[0.2em] px-6 py-4"
                data-testid="mobile-reserve-btn"
              >
                {t.nav.reserve}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
